/**
 * What the audit trail says about each row it shows.
 *
 * Out of `Audit.tsx` for the reason the Overview sentences are: `node --test`
 * refuses a `.tsx`, so anything left in the component is
 * beyond the reach of anything that runs. `Audit.tsx` held eight top-level
 * functions and had already produced two logic errors, both caught by a person
 * pulling a function out and running it by hand. This is that rung.
 *
 * `TrailRow` is written out rather than imported from `api.ts`. The import
 * would be tidier and would drag `config.ts` and `import.meta.env` in behind
 * it, and then this module would typecheck, build, and fail to load under the
 * runner. Third time that shape has come up, so it is spelled out.
 *
 * Every export returns a finished decision — a label with its tone, a panel
 * with its warning already in it. Nothing here hands back parts for a screen to
 * combine, because on the Overview that is exactly where the defect lived: two
 * numbers on a screen, added together, and every guard around them green.
 */

/** Structurally what `api.ts` returns. Kept apart so this module stays loadable. */
export interface TrailRow {
	actor_id: string;
	action: string;
	subject_id: string | null;
	detail: Record<string, unknown> | null;
	created_at: string;
}

export type Tone = "default" | "success" | "warning" | "error" | "info";

/**
 * What each action means in words, and how loudly to say it.
 *
 * The nine strings are the ones actually written by `access-store.ts` and
 * `tenancy-store.ts`. An unrecognised action still renders — a trail that hides
 * rows it does not recognise is worse than one that shows them plainly, because
 * the row you cannot classify is the one worth reading.
 */
const ACTIONS: Record<string, { label: string; tone: Tone }> = {
	"grants.replace": { label: "Grants replaced", tone: "warning" },
	"member.upsert": { label: "Operator seated", tone: "info" },
	"member.remove": { label: "Operator removed", tone: "info" },
	"org.register": { label: "Team registered", tone: "success" },
	"org.status": { label: "Team status changed", tone: "success" },
	"org.grants": { label: "Team assets lent", tone: "warning" },
	"org.member.seated": { label: "Team member seated", tone: "info" },
	"org.member.role": { label: "Team member re-roled", tone: "info" },
	"org.member.removed": { label: "Team member removed", tone: "info" },
};

/**
 * `detail` is a different shape per action, so it is rendered as facts rather
 * than parsed into one schema. Keys come back snake_case because the endpoint
 * returns raw rows; they are spelled out here rather than shown raw, since
 * `entity_type` on a screen is a leak of the database into the reader's face.
 */
const FIELDS: Record<string, string> = {
	name: "Team",
	status: "Status",
	role: "Role",
	org_id: "Team id",
	org_name: "Team",
	entity_type: "Asset type",
	entity_id: "Asset",
	entity_ids: "Assets",
	permission: "Permission",
	count: "How many",
	reason: "Reason",
};

const TYPES: Record<string, string> = {
	x_accounts: "ad accounts",
	agents: "agents",
	proxies: "proxies",
};

/** The badge and the line under it, decided together. */
export interface WhatCell {
	label: string;
	tone: Tone;
	/** "3 ad accounts · read", or "" when the row has nothing extra to say. */
	detail: string;
}

/**
 * Label, tone and summary in one answer.
 *
 * They were three expressions in the JSX — `ACTIONS[e.action]`, then `TONE[…]`
 * or `"default"`, then `e.action` as a fallback label — which is three chances
 * for an unrecognised action to be handled one way in the tone and another in
 * the text. One function, one lookup, one answer.
 */
export function whatCell(e: TrailRow): WhatCell {
	const known = ACTIONS[e.action];
	return {
		label: known?.label ?? e.action,
		tone: known?.tone ?? "default",
		detail: describe(e),
	};
}

function describe(e: TrailRow): string {
	const d = e.detail ?? {};
	const type = typeof d.entity_type === "string" ? (TYPES[d.entity_type] ?? d.entity_type) : null;
	// Again the shape, not the action name. A grant entry is one that names an
	// asset type and says how many — which is true of both grant actions today
	// and of any that gets added without this line being touched.
	const n = Array.isArray(d.entity_ids)
		? d.entity_ids.length
		: typeof d.count === "number"
			? d.count
			: null;
	if (type && n !== null) {
		const perm = typeof d.permission === "string" ? d.permission : "";
		return `${n} ${type} · ${perm}`;
	}
	if (typeof d.role === "string") return `as ${d.role}`;
	if (typeof d.status === "string") return `to ${d.status}`;
	if (typeof d.name === "string") return d.name;
	return "";
}

export interface DetailField {
	term: string;
	value: string;
}

export interface DetailPanel {
	heading: string;
	/** The "we only kept a count" caveat, already decided. `null` when it does not apply. */
	warning: string | null;
	fields: DetailField[];
}

/**
 * The expanded row, or `null` when there is nothing to expand.
 *
 * `null` is also the answer to "should the Detail button appear", and that is
 * the point of returning it from here. The screen used to ask two different
 * questions — the button on `Object.keys(detail).length > 0`, the panel on
 * `detail` being truthy — so a row with `detail: {}` had no button and an empty
 * panel behind it. One question now, asked once.
 */
export function detailPanel(e: TrailRow): DetailPanel | null {
	const d = e.detail;
	if (!d || Object.keys(d).length === 0) return null;
	return {
		heading: ACTIONS[e.action]?.label ?? e.action,
		/*
		 * Said here because the row above cannot say it. `replaceOrgGrants` and
		 * `replaceGrants` both write { entity_type, permission, count } and no
		 * identifiers, so "3 ad accounts · read" is the whole of what was kept. An
		 * operator looking for one account and not finding it would read that
		 * absence as "we never lent it", when the trail simply never held the
		 * answer — the same failure the 25-row note exists to prevent, one level
		 * down.
		 */
		warning: countsOnly(d)
			? "Which assets is not recorded — only how many. This entry cannot answer whether a particular account was among them."
			: null,
		fields: Object.entries(d).map(([k, v]) => ({ term: FIELDS[k] ?? k, value: format(k, v) })),
	};
}

/**
 * Does this entry count assets without naming them?
 *
 * Asked of the row, never of the action. `org.grants` used to mean "count only"
 * and is being changed to record `entity_ids`; `grants.replace` is not changing
 * with it, and neither change reaches rows already written — an append-only
 * record has no backfill. So one action name will describe both shapes at once,
 * and a set of action names would put "which assets is not recorded" above a
 * row that lists them.
 *
 * The field is the fact. Every row then describes itself, whichever side of the
 * change it was written on, and this needs no edit when the store changes.
 *
 * Presence, not length. An empty `entity_ids` is a record and not the absence
 * of one: it says nothing was lent, which answers "was this account among them"
 * — no. Treating `[]` as "no list" would put the banner denying a list exists
 * on top of a row that carries one, which is the confusion this whole function
 * was written to prevent. That is a regression this file has taken before, so
 * `audit-copy.test.ts` fails if the check becomes a length check.
 */
const namesAssets = (d: Record<string, unknown> | null) => Array.isArray(d?.entity_ids);

const countsOnly = (d: Record<string, unknown> | null) =>
	typeof d?.count === "number" && !namesAssets(d);

function format(k: string, v: unknown): string {
	if (k === "entity_type" && typeof v === "string") return TYPES[v] ?? v;
	// A list of ids is the answer to "was this account among them", so it is
	// rendered one per line. JSON.stringify would hand the reader quotes and
	// commas to pick through for the one thing they came to look for.
	// An empty list is an answer, so it is spelled out. Left blank it would read
	// as "the data did not survive" rather than "nothing was lent" — swapping one
	// silence for another, one field further down.
	if (Array.isArray(v)) return v.length ? v.map(String).join("\n") : "none — everything taken back";
	if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v);
	return JSON.stringify(v);
}

/** A uuid and, when one can be found, the name of whoever it belongs to. */
export interface Party {
	id: string | null;
	name?: string;
}

/**
 * Who did it and to whom, resolved against the two directories the screen has.
 *
 * The subject side is the one place the action name is still the right
 * question, and on purpose. Everything else here reads the shape of `detail`,
 * because a field is a fact and an action name is a label that can come to mean
 * two things. But no field says whether a `subject_id` is a person or a team —
 * the id alone is indistinguishable, both being uuids — and only the action
 * family settles it: `org.register`, `org.status` and `org.grants` act on a
 * team, everything else on a person. Falling through to the other map keeps a
 * mislabelled row readable instead of blank.
 *
 * A miss is expected rather than exceptional, and there are two separate
 * reasons for one, worth keeping apart because they mean different things.
 *
 * Sometimes it is the guarantee working. The record outlives the member and the
 * team it describes, so an operator removed last month is still named in rows
 * they wrote and will never appear in `members()` again.
 *
 * And sometimes it is structural and permanent. The subject of an
 * `org.member.*` row is a person in `org_member` — a team's own — while
 * `members()` returns `access_member`, which is ours. Two ladders, two tables,
 * and `principal.ts` keeps a caller in at most one, so those subjects are not
 * merely absent today: they cannot appear. Naming them needs
 * `/orgs/:id/members`, and the `org_id` to ask with is sitting in that row's own
 * `detail`. Filed rather than done here.
 *
 * Either way the uuid stands and the row still reads.
 */
export function parties(
	e: TrailRow,
	byUser: ReadonlyMap<string, string>,
	byOrg: ReadonlyMap<string, string>,
): { actor: Party; subject: Party } {
	const aboutATeam = e.action.startsWith("org.") && !e.action.startsWith("org.member");
	return {
		actor: { id: e.actor_id, name: byUser.get(e.actor_id) },
		subject: {
			id: e.subject_id,
			name: e.subject_id
				? aboutATeam
					? byOrg.get(e.subject_id)
					: (byUser.get(e.subject_id) ?? byOrg.get(e.subject_id))
				: undefined,
		},
	};
}

/**
 * The rows carry no id — the endpoint selects five columns and the primary key
 * is not among them — so identity is composed from what is there. Two entries
 * sharing an actor, an action, a subject and a timestamp are the same event.
 */
export const trailKey = (e: TrailRow) =>
	`${e.created_at}|${e.actor_id}|${e.action}|${e.subject_id ?? ""}`;

/**
 * Relative for the recent ones, absolute once "3 days ago" stops being useful.
 *
 * `now` is a parameter and not `Date.now()` inside, so that this is a function
 * of its inputs and a runner can ask it about a row from last Tuesday.
 */
export function whenLabel(iso: string, now: number): string {
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return iso;
	const mins = Math.round((now - then) / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
	if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
	return new Date(then).toLocaleDateString();
}

/** The endpoint's ceiling. No `?limit` is accepted, so this is not a choice. */
export const PAGE = 25;

/**
 * The note under a full page, or `null` when the page is not full.
 *
 * A trail that silently shows a slice reads as a complete history, and an
 * operator checking "did we ever lend this" would draw the wrong conclusion
 * from its absence. The same failure `detailPanel`'s warning exists to prevent,
 * one level up.
 */
export function footerNote(rowCount: number): string | null {
	return rowCount >= PAGE
		? `Showing the ${PAGE} most recent entries — the endpoint does not yet accept a range, so older changes are recorded but not reachable from here.`
		: null;
}
